/**
 * A composer's voice notes (Figma 05 · Voice): talk, the words are written
 * down into the field, then checked and sent like typing. Off for people who
 * can't edit, and where the browser can't record. Home and the Workshop's
 * composers share it.
 */
import { useEffect, useState } from "react";
import { isAxiosError } from "axios";
import { queue } from "@/components/Toast";
import { usePermissions } from "@/hooks/usePermissions";
import { useVoiceRecorder, voiceSupported } from "./useVoiceRecorder";
import { transcribeVoiceNote } from "./voiceTranscribe";
import type { ComposerVoice } from "./RailComposer";

export function useComposerVoice(brandId: string, setText: (fn: (prev: string) => string) => void): ComposerVoice | undefined {
  const { canEdit } = usePermissions();
  const [canTalk] = useState(voiceSupported);
  const recorder = useVoiceRecorder();
  const [stage, setStage] = useState<"idle" | "writing" | "checking">("idle");
  const stop = async () => {
    const file = await recorder.stop();
    if (!file) return;
    setStage("writing");
    try {
      const words = await transcribeVoiceNote(brandId, file);
      if (!words) throw new Error("No words were heard. Try again, or type instead.");
      setText((prev) => (prev.trim() ? `${prev.trimEnd()} ${words}` : words));
      setStage("checking");
    } catch (error) {
      setStage("idle");
      queue.add({
        title: "Couldn't write that down",
        description:
          (isAxiosError(error) ? (error.response?.data as { message?: string } | undefined)?.message : null) ??
          (error instanceof Error ? error.message : null) ??
          "Try again, or type instead.",
        variant: "error",
      });
    }
  };
  // A note that runs to the cap stops itself, through the same path as Stop.
  useEffect(() => {
    recorder.autoStop.current = () => void stop();
  });
  if (!canEdit || !canTalk) return undefined;
  return {
    phase: recorder.recording ? "recording" : stage,
    levels: recorder.levels,
    elapsedMs: recorder.elapsedMs,
    onStart: () => {
      recorder.start().catch(() =>
        queue.add({
          title: "The microphone is off",
          description: "Allow the microphone for this site, then try again.",
          variant: "error",
        }),
      );
    },
    onStop: () => void stop(),
    onCancel: recorder.cancel,
  };
}
